/**
 * Mirror of the auth Zod schemas from cosmo-bud-portal-backend.
 *
 * THESE MUST STAY IN SYNC. Source of truth:
 *   ../../../../cosmo-bud-portal-backend/src/modules/auth/dto/login.dto.ts
 *   ../../../../cosmo-bud-portal-backend/src/modules/auth/dto/refresh.dto.ts
 *   ../../../../cosmo-bud-portal-backend/src/modules/auth/dto/change-password.dto.ts
 *
 * The CI script `scripts/diff-schemas.mjs` greps the portal-backend files
 * and compares the Zod chain bodies (between `z.object({` and the closing
 * `}` of the .object call) against the bodies here. Any drift fails CI.
 *
 * If you intentionally widen/tighten validation here for UX (e.g. stricter
 * client-side hint), DO NOT modify these mirrored schemas — add a separate
 * client-only schema that extends from these.
 */
import { z } from 'zod';

// >>> portal-backend:login.dto.ts
// Client-only schema with user-friendly error messages
// (Backend validation: email.min(3).max(254).email(), password.min(1).max(256))
export const LoginSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .min(3, 'Please enter a valid email')
    .max(254)
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required').max(256),
});
// <<< portal-backend:login.dto.ts

// >>> portal-backend:refresh.dto.ts
export const RefreshSchema = z.object({
  refreshToken: z.string().min(1).max(2048).optional(),
});
// <<< portal-backend:refresh.dto.ts

// >>> portal-backend:change-password.dto.ts
export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required').max(256),
    newPassword: z
      .string()
      .min(10, 'New password must be at least 10 characters')
      .max(256),
  })
  .refine((d) => d.currentPassword !== d.newPassword, {
    path: ['newPassword'],
    message: 'New password must differ from the current password',
  });
// <<< portal-backend:change-password.dto.ts

export const ForgotPasswordSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .min(3, 'Please enter a valid email')
    .max(254)
    .email('Please enter a valid email address'),
});

// >>> portal-backend:set-password-token.dto.ts
export const SetPasswordTokenSchema = z.object({
  token: z.string().min(1).max(500),
  newPassword: z.string().min(10).max(128),
});
// <<< portal-backend:set-password-token.dto.ts

export type LoginInput = z.infer<typeof LoginSchema>;
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;
export type SetPasswordTokenInput = z.infer<typeof SetPasswordTokenSchema>;
